import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Clef } from '@mt/domain';
import { parsePitchId } from '@mt/domain';
import { cn } from '../primitives.tsx';

/**
 * Real music notation rendered with VexFlow (SVG): correct clefs, ledger lines
 * and accidentals. Lazy-loads VexFlow + the Bravura font (bundled, no network)
 * and re-lays out whenever the container width changes.
 *
 * Accessibility: the SVG is hidden from assistive tech; the wrapper exposes
 * `label`, which callers must write so it never reveals a quiz answer.
 */

type VexFlowModule = typeof import('vexflow/bravura');
type VexStave = InstanceType<VexFlowModule['Stave']>;
let vexflowPromise: Promise<VexFlowModule> | null = null;

export function loadVexFlow(): Promise<VexFlowModule> {
  vexflowPromise ??= import('vexflow/bravura').then(async (mod) => {
    if (typeof document !== 'undefined' && document.fonts) {
      await Promise.race([document.fonts.load('40px Bravura'), new Promise((r) => setTimeout(r, 2500))]);
    }
    return mod;
  });
  return vexflowPromise;
}

export type NoteMark = 'correct' | 'incorrect' | 'focus' | null;

export interface StaffProps {
  /** Clef of the staff the notes are written on. With `grand`, "treble" or "bass". */
  clef: Clef;
  /** Pitch ids to draw left to right, e.g. ["C4", "F#4"]. */
  notes: string[];
  label: string;
  /** Optional per-note color treatment (feedback). */
  marks?: NoteMark[];
  /**
   * Visual scale relative to the container; staff line spacing grows with width.
   * "xl" is the player's enlarged view: it uses more of the width and height.
   */
  size?: 'md' | 'lg' | 'xl';
  /** Draw a grand staff (treble above bass, joined by a brace). */
  grand?: boolean;
  /** With `grand`: the staff for each note when notes sit on different staves (e.g. a range preview). */
  noteClefs?: Clef[];
  className?: string;
}

const LOGICAL_HEIGHT = 150;
/** Grand staff: treble top line at 50, bass staff 100 lower, room for three ledger lines below the bass. */
const GRAND_LOGICAL_HEIGHT = 250;
const GRAND_BASS_OFFSET = 100;
/** Room on the left for the brace. */
const GRAND_LEFT = 24;
const MIN_SCALE = 1.15;

function vexKey(id: string): { key: string; accidental: string | null } {
  const p = parsePitchId(id);
  if (!p) throw new Error(`Invalid pitch ${id}`);
  const acc = p.accidental === 'sharp' ? '#' : p.accidental === 'flat' ? 'b' : '';
  return { key: `${p.letter.toLowerCase()}${acc}/${p.octave}`, accidental: acc || null };
}

const MARK_COLOR: Record<Exclude<NoteMark, null>, string> = {
  correct: 'var(--color-fern)',
  incorrect: 'var(--color-rose)',
  focus: 'var(--color-primary)',
};

export function Staff({ clef, notes, label, marks, size = 'lg', grand = false, noteClefs, className }: StaffProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      setWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Stable string keys so the effect re-runs only when the drawn content changes.
  const notesKey = notes.join(',');
  const marksKey = (marks ?? []).join(',');
  const noteClefsKey = (noteClefs ?? []).join(',');

  useEffect(() => {
    let cancelled = false;
    const el = hostRef.current;
    const noteList = notesKey ? notesKey.split(',') : [];
    const markList = marksKey.split(',').map((m) => (m ? (m as NoteMark) : null));
    const clefList = noteClefsKey ? (noteClefsKey.split(',') as Clef[]) : [];
    if (!el || width < 40) return;
    loadVexFlow()
      .then((VF) => {
        if (cancelled || !hostRef.current) return;
        const target = hostRef.current;
        target.innerHTML = '';
        // Scale so staff lines are comfortably spaced on phones and larger screens.
        const xl = size === 'xl';
        const logicalHeight = grand ? GRAND_LOGICAL_HEIGHT : LOGICAL_HEIGHT;
        const perNote = noteList.length <= 1 ? (xl ? 90 : 150) : 70;
        const needed = (xl ? 100 : 110) + (grand ? GRAND_LEFT : 0) + perNote * Math.max(1, noteList.length);
        const preferred = xl
          ? Math.min(3.4, Math.max(MIN_SCALE, width / 190))
          : size === 'lg'
            ? Math.min(2.2, Math.max(MIN_SCALE, width / 260))
            : Math.min(1.7, Math.max(1, width / 300));
        // Short viewports (landscape phones): keep the staff to ~35% of the height so answers stay on screen.
        // Side-by-side landscape layouts can give the staff more; the enlarged view takes more still.
        const sideBySide = typeof window !== 'undefined' && window.matchMedia?.('(orientation: landscape) and (max-height: 520px)').matches;
        // A grand staff is nearly twice as tall, so it gets a larger share to keep lines as readable.
        const heightShare = xl ? (sideBySide ? 0.8 : grand ? 0.55 : 0.5) : sideBySide ? 0.6 : grand ? 0.46 : 0.35;
        const byHeight = typeof window !== 'undefined' ? (window.innerHeight * heightShare) / logicalHeight : preferred;
        const scale = Math.max(Math.min(preferred, width / needed, byHeight), grand ? 0.6 : 0.8);
        const logicalWidth = width / scale;
        const heightPx = Math.round(logicalHeight * scale);

        const renderer = new VF.Renderer(target, VF.Renderer.Backends.SVG);
        renderer.resize(width, heightPx);
        const ctx = renderer.getContext();
        ctx.scale(scale, scale);
        ctx.setFillStyle('currentColor');
        ctx.setStrokeStyle('currentColor');

        const makeStave = (x: number, y: number, staveClef: Clef): VexStave => {
          const stave = new VF.Stave(x, y, logicalWidth - x - 6, {
            leftBar: grand,
            rightBar: false,
            spaceAboveStaffLn: 5,
            spaceBelowStaffLn: 5,
          });
          stave.setStyle({ strokeStyle: 'currentColor', fillStyle: 'currentColor' });
          stave.setDefaultLedgerLineStyle({ strokeStyle: 'currentColor', lineWidth: 1.6 });
          stave.addClef(staveClef);
          return stave;
        };

        const staves = new Map<Clef, VexStave>();
        if (grand) {
          const treble = makeStave(GRAND_LEFT, 0, 'treble');
          const bass = makeStave(GRAND_LEFT, GRAND_BASS_OFFSET, 'bass');
          // Line the notes up across both staves.
          const noteStart = Math.max(treble.getNoteStartX(), bass.getNoteStartX());
          treble.setNoteStartX(noteStart);
          bass.setNoteStartX(noteStart);
          treble.setContext(ctx).draw();
          bass.setContext(ctx).draw();
          for (const type of ['brace', 'singleLeft'] as const) {
            new VF.StaveConnector(treble, bass).setType(type).setContext(ctx).draw();
          }
          staves.set('treble', treble).set('bass', bass);
        } else {
          const stave = makeStave(6, 0, clef);
          stave.setContext(ctx).draw();
          staves.set(clef, stave);
        }

        const placed = noteList.map((id, index) => {
          const wanted = grand ? (clefList[index] ?? clef) : clef;
          const staveClef: Clef = staves.has(wanted) ? wanted : grand ? 'treble' : clef;
          const stave = staves.get(staveClef) as VexStave;
          const { key, accidental } = vexKey(id);
          const note = new VF.StaveNote({ keys: [key], duration: 'w', clef: staveClef });
          if (accidental) note.addModifier(new VF.Accidental(accidental), 0);
          const mark = markList[index];
          const color = mark ? MARK_COLOR[mark] : 'currentColor';
          note.setStyle({ fillStyle: color, strokeStyle: color });
          note.setLedgerLineStyle({ strokeStyle: 'currentColor', lineWidth: 1.6 });
          return { note, stave };
        });
        // Spread notes evenly across the staff (a single note sits centered),
        // positioning each with its own tick/modifier context.
        placed.forEach(({ note, stave }, i) => {
          const span = stave.getNoteEndX() - stave.getNoteStartX();
          const modifiers = new VF.ModifierContext();
          note.addToModifierContext(modifiers);
          const tick = new VF.TickContext();
          tick.addTickable(note).preFormat();
          const center = span * ((i + 0.5) / placed.length);
          tick.setX(Math.max(0, center - note.getWidth() / 2));
          note.setContext(ctx).setStave(stave);
          note.draw();
        });
        const svg = target.querySelector('svg');
        if (svg) {
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
          svg.style.display = 'block';
          svg.style.maxWidth = '100%';
        }
        setReady(true);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError('The music staff could not be drawn. Reload to try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [clef, notesKey, marksKey, noteClefsKey, grand, width, size]);

  return (
    <div role="img" aria-label={label} className={cn('relative w-full text-ink', className)}>
      <div ref={hostRef} className={cn('w-full transition-opacity duration-150', ready ? 'opacity-100' : 'opacity-0')} style={{ minHeight: grand ? 180 : 120 }} />
      {!ready && !error && (
        <div aria-hidden="true" className="absolute inset-0 flex flex-col justify-center gap-[9%] px-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-px w-full bg-line-strong" />
          ))}
        </div>
      )}
      {error && <p className="absolute inset-0 grid place-items-center text-sm text-rose-ink">{error}</p>}
    </div>
  );
}
